import { useState, type ReactNode } from "react";
import type { AuditFile } from "./india";
import {
  carryForwardEligibility,
  carryForwardQuery,
  reconciliationSuggestions,
  createQueryFromSuggestion,
  effectiveMovementThreshold,
} from "./query-assistance";
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="afi-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
export function QueryAssistance({
  file,
  files,
  actor,
  onSave,
}: {
  file: AuditFile;
  files: AuditFile[];
  actor: string;
  onSave: (file: AuditFile, action: string) => void;
}) {
  const [sourceId, setSourceId] = useState("");
  const [queryId, setQueryId] = useState("");
  const [owner, setOwner] = useState("");
  const [contact, setContact] = useState("");
  const [due, setDue] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [threshold, setThreshold] = useState("");
  const options = threshold.trim()
    ? { movementThreshold: Number(threshold) }
    : {};
  let suggestions: ReturnType<typeof reconciliationSuggestions> = [];
  let screeningError = "";
  let effective: number | undefined;
  try {
    suggestions = reconciliationSuggestions(file, options);
    effective = effectiveMovementThreshold(file, options);
  } catch (e) {
    screeningError = e instanceof Error ? e.message : String(e);
  }
  const source = files.find((f) => f.id === sourceId);
  const eligibility = source ? carryForwardEligibility(source, file) : [];
  const assignment = { owner, clientContact: contact, dueDate: due, reason };
  function act(work: () => void) {
    try {
      work();
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setNotice("");
    }
  }
  function save(q: AuditFile["queries"][number], action: string) {
    onSave({ ...file, queries: [...file.queries, q] }, action);
    setNotice(
      `${q.reference} created as a draft. Open Queries to review and issue it.`,
    );
  }
  return (
    <div>
      <h2>Query assistance</h2>
      <p>
        Review earlier issues and trial-balance exceptions, then decide which
        questions need follow-up. Suggestions do not establish a misstatement or
        a completed audit procedure.
      </p>
      {error && (
        <div className="afi-error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="afi-query-note" role="status">
          {notice}
        </div>
      )}
      <section className="afi-panel">
        <header>
          <h3>Current-period assignment and reassessment</h3>
        </header>
        <div className="afi-grid">
          <Field label="Current audit owner">
            <input value={owner} onChange={(e) => setOwner(e.target.value)} />
          </Field>
          <Field label="Current client contact">
            <input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
          </Field>
          <Field label="Current response due date">
            <input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Why this needs current-period follow-up">
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <small>
          Record a substantive reason. Every generated query starts as a fresh
          draft and blocks completion until resolved. Reassess its financial
          effect; screening amounts are not assumed misstatements.
        </small>
      </section>
      <section className="afi-panel">
        <header>
          <h3>Prior-year query carry-forward</h3>
        </header>
        <p>
          Choose an earlier file for the same registered client, framework and
          reporting scope. Evidence, responses, conclusions and approvals
          require fresh assessment.
        </p>
        <div className="afi-grid">
          <Field label="Source audit file">
            <select
              value={sourceId}
              onChange={(e) => {
                setSourceId(e.target.value);
                setQueryId("");
                setNotice("");
              }}
            >
              <option value="">Choose an earlier audit file</option>
              {files
                .filter((f) => f.id !== file.id)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.clientName} · {f.periodEnd} · {f.framework}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Prior-year query">
            <select
              value={queryId}
              onChange={(e) => setQueryId(e.target.value)}
              disabled={!source || eligibility.length > 0}
            >
              <option value="">Select query to reassess</option>
              {source?.queries.map((q) => (
                <option value={q.id} key={q.id}>
                  {q.reference} · {q.title} ({q.status})
                </option>
              ))}
            </select>
          </Field>
        </div>
        {eligibility.length > 0 && (
          <ul>
            {eligibility.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        )}
        <button
          className="afi-primary"
          disabled={!source || !queryId || eligibility.length > 0}
          onClick={() =>
            act(() => {
              if (!source) throw new Error("Select a source file.");
              save(
                carryForwardQuery(
                  { ...assignment, source, target: file, queryId },
                  actor,
                ),
                "Carried forward a query after current-period reassessment",
              );
            })
          }
        >
          Create current-period query draft
        </button>
      </section>
      <section className="afi-panel">
        <header>
          <h3>Reconciliation and movement suggestions</h3>
        </header>
        <div className="afi-grid">
          <Field label="Absolute movement threshold (INR, optional)">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="Use documented performance materiality"
            />
          </Field>
          <div>
            <p>
              Movement screening: at least 25% and above the absolute threshold.
              New balances are screened against the absolute threshold.
            </p>
            <small>
              {effective
                ? `Current absolute threshold: ₹${effective.toLocaleString("en-IN")}`
                : "Set a positive threshold or complete materiality to enable movement screening."}
            </small>
          </div>
        </div>
        {screeningError && <p role="alert">{screeningError}</p>}
        {!file.tb.length && (
          <p>
            Import a trial balance to see account mapping and balance movement
            prompts.
          </p>
        )}
        {file.tb.length > 0 && !suggestions.length && !screeningError && (
          <p>
            No new prompts under these rules. Existing prompts stay suppressed;
            reopen their queries when further work is needed.
          </p>
        )}
        <div className="afi-query-messages">
          {suggestions.map((s) => (
            <article className="afi-query-message" key={s.id}>
              <header>
                <h3>{s.title}</h3>
                <span className="afi-tag">{s.priority}</span>
              </header>
              <p className="afi-query-text">{s.description}</p>
              <button
                onClick={() =>
                  act(() =>
                    save(
                      createQueryFromSuggestion(
                        { ...assignment, file, suggestionId: s.id, options },
                        actor,
                      ),
                      "Created a query from a reviewed trial-balance screening prompt",
                    ),
                  )
                }
              >
                Review and create draft: {s.title}
              </button>
            </article>
          ))}
        </div>
        <small>
          Checks use the imported trial balance only. GST returns, bank feeds
          and source-ledger reconciliations are not connected to this screen.
        </small>
      </section>
    </div>
  );
}
